import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./Signup.css";

/* ================= API BASE HELPER ================= */
const normalizeBase = (value) =>
  String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ================= STORAGE KEYS ================= */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_USER_KEYS = [
  "user",
  "currentUser",
  "fullName",
  "name",
  "userName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "isLoggedIn",
];

/* ================= AUTH HELPERS ================= */
const clearAuthStorage = () => {
  [...AUTH_TOKEN_KEYS, ...AUTH_USER_KEYS].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const notifyAuthChanged = (action = "signup") => {
  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: {
        source: "Signup",
        action,
      },
    })
  );

  window.dispatchEvent(
    new CustomEvent("login-success", {
      detail: {
        source: "Signup",
        action,
      },
    })
  );
};

const saveAuthData = ({ token, user }) => {
  if (!token) return;

  clearAuthStorage();

  localStorage.setItem("token", token);
  localStorage.setItem("authToken", token);
  localStorage.setItem("accessToken", token);
  localStorage.setItem("isLoggedIn", "true");

  if (user && typeof user === "object") {
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("currentUser", JSON.stringify(user));

    if (user.fullName) {
      localStorage.setItem("fullName", user.fullName);
      localStorage.setItem("name", user.fullName);
      localStorage.setItem("userName", user.fullName);
    }

    if (user.email) {
      localStorage.setItem("email", user.email);
      localStorage.setItem("userEmail", user.email);
      localStorage.setItem("user_email", user.email);
    }
  }

  notifyAuthChanged("signup-login");
};

/* ================= VALIDATION ================= */
const isValidEmail = (email = "") => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidPhone = (phone = "") => {
  const clean = String(phone || "").replace(/[^\d+]/g, "");
  return clean.length >= 8 && clean.length <= 15;
};

function Signup() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
    setMessage("");
  };

  const validate = () => {
    const fullName = formData.fullName.trim();
    const email = formData.email.trim().toLowerCase();
    const phone = formData.phone.trim();
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;

    if (!fullName || !email || !phone || !password || !confirmPassword) {
      return "All fields are required.";
    }

    if (fullName.length < 2 || fullName.length > 150) {
      return "Full name must be between 2 and 150 characters.";
    }

    if (!isValidEmail(email)) {
      return "Please enter a valid email address.";
    }

    if (!isValidPhone(phone)) {
      return "Please enter a valid phone number.";
    }

    if (password.length < 6) {
      return "Password must be at least 6 characters.";
    }

    if (password !== confirmPassword) {
      return "Passwords do not match.";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setMessage("");

    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: formData.fullName.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false) {
        setError(data?.message || "Signup failed.");
        return;
      }

      /*
        Backend updated authController may return token directly
        when REQUIRE_EMAIL_VERIFICATION=false.
        If token exists, login user immediately.
        If token does not exist, ask user to verify email/login.
      */
      if (data?.token) {
        saveAuthData({
          token: data.token,
          user: data?.data || {},
        });

        setMessage("Account created successfully. Redirecting to dashboard...");

        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 200);

        return;
      }

      setMessage(
        data?.message ||
          "Account created successfully. Please check your email and verify your account before login."
      );

      setFormData({
        fullName: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        navigate("/signin", { replace: true });
      }, 1200);
    } catch (err) {
      console.error(err);
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <section className="signup-section">
        <div className="signup-container">
          <div className="signup-left">
            <span className="signup-badge">Create your account</span>

            <h1>Start your domain journey today</h1>

            <p>
              Create an account to search domains, buy hosting, manage DNS,
              track payments and access your dashboard.
            </p>
          </div>

          <div className="signup-card">
            <h2>Create Account</h2>

            {error ? <div className="auth-error">{error}</div> : null}
            {message ? <div className="auth-success">{message}</div> : null}

            <form onSubmit={handleSubmit} className="signup-form">
              <div className="signup-input-group">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="name"
                />
              </div>

              <div className="signup-input-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="signup-input-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="tel"
                />
              </div>

              <div className="signup-input-group">
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <div className="signup-input-group">
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className="signup-btn" disabled={loading}>
                {loading ? "Creating..." : "Create Account"}
              </button>
            </form>

            <div className="signup-extra">
              Already have an account? <Link to="/signin">Sign In</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Signup;